import React, {useCallback, useMemo, useState} from 'react'
import {Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar} from '@mui/material'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import {ThemeColorsHospitalic} from '@/assets/styles/color/ThemeColorsHospitalic'
import {useLazyQuery, useMutation} from '@apollo/client'
import Alert from '@mui/material/Alert'
import {DATASET_LIST_QUERY, DELETE_DATASET} from '@/graphql/query/queries'
import {SelectableDatasetInfo} from '@/components/pages/DataInfoPage/types'

type DeleteDialogProps = {
    datasets?: SelectableDatasetInfo[];
    open: boolean;
    onClose: () => void;
}

const DeleteDatasetDialog = ({datasets, open, onClose}: DeleteDialogProps) => {
    const [deleteErrors, setDeleteErrors] = useState<string[]>([])
    const [loading, setLoading] = useState<boolean>(false)

    const [_getDatasetList, {
        refetch: refetchDatasets
    }] = useLazyQuery(DATASET_LIST_QUERY, {notifyOnNetworkStatusChange: true, fetchPolicy: 'network-only'})
    const [deleteDataset] = useMutation(DELETE_DATASET)

    const areSomeSelected = useMemo((): boolean => {
        return !!datasets && datasets.some(ds => ds.selected)
    }, [datasets])

    const handleClose = useCallback(() => {
        if (onClose) {
            onClose()
        }
    }, [onClose])

    const handleConfirm = useCallback(async () => {
        if(areSomeSelected) {
            setLoading(true)
            const deletePromises = datasets!.filter(ds => ds.selected)
                .map(ds => deleteDataset({variables: {datasetId: ds.dataset_id}})
                    .then(data => ({id: ds.dataset_id, data, error: null}))
                    .catch(error => ({id: ds.dataset_id, data: null, error}))
                )

            const results = await Promise.all(deletePromises)

            results.forEach(result => {
                if(result.error || (result.data?.errors && result.data.errors.length > 0)) {
                    setDeleteErrors(prev => [...prev, result.id])
                }
            })

            await refetchDatasets()
            setLoading(false)
            handleClose()
        }
    }, [datasets, areSomeSelected, handleClose])

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title" variant="h5">
                    {areSomeSelected ? 'Are you sure you want to delete following datasets?' : 'No datasets selected'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        {areSomeSelected ? (
                            datasets!.filter(ds => ds.selected).map(ds => (
                                <Typography key={ds.dataset_id} color={ThemeColorsHospitalic.TEXT_PRIMARY}>
                                    Dataset id: {ds?.dataset_id},
                                    Dataset name: {ds?.dataset_name}
                                </Typography>
                            ))
                        ) : (
                            <Typography color={ThemeColorsHospitalic.TEXT_PRIMARY}>
                                Please select datasets for deletion in the dataset table.
                            </Typography>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} variant="outlined" size="small">Back</Button>
                    <Button onClick={handleConfirm} loading={loading} autoFocus variant="contained" size="small"
                        disabled={!areSomeSelected}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={deleteErrors.length > 0}
                autoHideDuration={3000}
                onClose={() => setDeleteErrors([])}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}>
                <Alert
                    onClose={() => setDeleteErrors([])}
                    severity="error"
                    variant="filled"
                    sx={{width: '100%'}}
                >
                    Error deleting datasets with id: {deleteErrors.join(', ')}
                </Alert>
            </Snackbar>
        </>
    )
}

export default DeleteDatasetDialog